import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  FiFile, FiSearch, FiDownload, FiEye, FiTrash2,
  FiArrowLeft, FiHome, FiFileText, FiMail, FiCheckSquare, FiSquare,
  FiChevronLeft, FiChevronRight, FiMaximize2
} from 'react-icons/fi';
import { documentsService, Document } from '../services/documents.service';
import { propertiesService, Property } from '../services/properties.service';
import { DocumentViewer } from '../components/DocumentViewer';
import { EmailDocumentsModal } from '../components/EmailDocumentsModal';
import { toast } from 'react-hot-toast';
import { safeFormatDate } from '../utils/dateUtils';
import { tw, monoFont } from '../utils/terminalTheme';

const PropertyDocuments: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [property, setProperty] = useState<Property | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState<Document | null>(null);
  const [fullscreenDoc, setFullscreenDoc] = useState<Document | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [actualPropertyId, setActualPropertyId] = useState<number | null>(null);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);

  // Pagination
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalLoaded, setTotalLoaded] = useState(0);
  const PAGE_SIZE = 200;

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      let propertyData;
      let propertyId: number;

      const numericId = parseInt(id!);
      if (!isNaN(numericId)) {
        try {
          propertyData = await propertiesService.getProperty(numericId);
          propertyId = numericId;
        } catch (err: any) {
          if (err.response?.status === 404) {
            propertyData = await propertiesService.getPropertyByEmailPrefix(id!);
            propertyId = propertyData.id;
          } else {
            throw err;
          }
        }
      } else {
        propertyData = await propertiesService.getPropertyByEmailPrefix(id!);
        propertyId = propertyData.id;
      }

      setProperty(propertyData);
      setActualPropertyId(propertyId);

      // Load all documents (up to 500)
      const docs = await documentsService.getDocuments({
        property_id: propertyId,
        limit: 500
      });
      setDocuments(docs);
      setTotalLoaded(docs.length);
      setHasMore(docs.length >= 500);

      // Auto-select first document for preview
      if (docs.length > 0 && !selectedDoc) {
        setSelectedDoc(docs[0]);
      }
    } catch (error: any) {
      console.error('Error loading data:', error);
      if (error.response?.status === 404) {
        toast.error('Property "' + id + '" not found');
      } else {
        toast.error('Failed to load documents');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (doc: Document) => {
    try {
      const response = await documentsService.downloadDocument(doc.id.toString());
      const mimeType = doc.mime_type || 'application/pdf';
      const blob = new Blob([response], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.suggested_copy_destination_name || doc.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleDelete = async (doc: Document) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      await documentsService.deleteDocument(doc.id.toString());
      toast.success('Document deleted');
      if (selectedDoc?.id === doc.id) {
        setSelectedDoc(null);
      }
      loadData();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const getDocumentTypeBadge = (type: string) => {
    const typeColors: Record<string, string> = {
      expense: 'bg-green-500/15 text-green-400 border-green-500/30',
      contract: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
      hoa_document: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30',
      proposal: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
      credit_card_statement: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    };
    const colors = typeColors[type] || 'bg-gray-500/15 text-gray-400 border-gray-500/30';
    return (
      <span className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-mono border ${colors}`}>
        {type}
      </span>
    );
  };

  const handleCheckboxClick = useCallback((docId: string, index: number, event: React.MouseEvent) => {
    event.stopPropagation();

    setSelectedIds(prev => {
      const newSet = new Set(prev);

      if (event.shiftKey && lastSelectedIndex !== null) {
        const start = Math.min(lastSelectedIndex, index);
        const end = Math.max(lastSelectedIndex, index);
        for (let i = start; i <= end; i++) {
          newSet.add(filteredDocuments[i].id);
        }
      } else {
        if (newSet.has(docId)) {
          newSet.delete(docId);
        } else {
          newSet.add(docId);
        }
      }

      return newSet;
    });

    setLastSelectedIndex(index);
  }, [lastSelectedIndex]);

  const handleSelectAll = () => {
    if (selectedIds.size === filteredDocuments.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredDocuments.map(d => d.id)));
    }
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const handleEmailSelected = () => {
    if (selectedIds.size === 0) {
      toast.error('Please select at least one document');
      return;
    }
    setShowEmailModal(true);
  };

  const handleEmailSuccess = () => {
    setShowEmailModal(false);
    clearSelection();
    toast.success('Documents shared successfully!');
  };

  // Navigate prev/next in file list
  const navigateDoc = (direction: 'prev' | 'next') => {
    if (!selectedDoc) return;
    const currentIdx = filteredDocuments.findIndex(d => d.id === selectedDoc.id);
    if (currentIdx === -1) return;
    const newIdx = direction === 'prev' ? currentIdx - 1 : currentIdx + 1;
    if (newIdx >= 0 && newIdx < filteredDocuments.length) {
      setSelectedDoc(filteredDocuments[newIdx]);
    }
  };

  const filteredDocuments = documents.filter(doc =>
    searchQuery === '' ||
    doc.filename.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.suggested_copy_destination_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.email_subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.extracted_data?.vendor?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectedDocuments = filteredDocuments.filter(d => selectedIds.has(d.id));

  const currentDocIndex = selectedDoc ? filteredDocuments.findIndex(d => d.id === selectedDoc.id) : -1;

  if (isLoading) {
    return (
      <div className={tw.loadingContainer}>
        <div className={tw.loading}></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-950">
      {/* Header Bar */}
      <div className="flex-shrink-0 border-b border-gray-800 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to={'/properties/' + (actualPropertyId || id)}
              className="flex items-center gap-1.5 text-gray-500 hover:text-cyan-400 transition-colors text-xs font-mono"
            >
              <FiArrowLeft className="w-3.5 h-3.5" />
              Back
            </Link>
            <div>
              <h1 className="text-base font-bold font-mono text-white" style={monoFont}>
                <span className="text-cyan-400">~/</span>{property?.name || 'docs'}<span className="text-gray-600">/documents</span>
              </h1>
              <p className="text-gray-600 text-[10px] font-mono">{documents.length} documents{hasMore ? '+' : ''}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Selection info */}
            {selectedIds.size > 0 && (
              <span className="text-cyan-400 font-mono text-[10px] mr-2">{selectedIds.size} selected</span>
            )}
            <button
              onClick={handleEmailSelected}
              disabled={selectedIds.size === 0}
              className={`flex items-center gap-1.5 text-[11px] font-mono px-3 py-1.5 rounded ${
                selectedIds.size > 0
                  ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/40 hover:bg-cyan-500/30'
                  : 'bg-gray-800 text-gray-600 border border-gray-700 cursor-not-allowed'
              }`}
            >
              <FiMail className="w-3 h-3" />
              Email
            </button>
          </div>
        </div>
      </div>

      {/* Main Split Pane */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: File List */}
        <div className="w-80 xl:w-96 flex-shrink-0 border-r border-gray-800 flex flex-col bg-gray-900/50">
          {/* Search */}
          <div className="p-2 border-b border-gray-800/50">
            <div className="relative">
              <FiSearch className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-600 w-3 h-3" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Filter documents..."
                className="w-full bg-gray-800/80 border border-gray-700/50 rounded px-3 py-1.5 pl-8 text-xs font-mono text-white placeholder-gray-600 focus:outline-none focus:border-cyan-500/50"
              />
            </div>
            <div className="flex items-center justify-between mt-1.5 px-1">
              <button
                onClick={handleSelectAll}
                className="flex items-center gap-1 text-gray-500 hover:text-white transition-colors text-[10px] font-mono"
              >
                {selectedIds.size === filteredDocuments.length && filteredDocuments.length > 0 ? (
                  <FiCheckSquare className="w-3 h-3 text-cyan-400" />
                ) : (
                  <FiSquare className="w-3 h-3" />
                )}
                {selectedIds.size > 0 ? `${selectedIds.size}/${filteredDocuments.length}` : `${filteredDocuments.length} docs`}
              </button>
              {selectedIds.size > 0 && (
                <button onClick={clearSelection} className="text-gray-600 hover:text-white text-[10px] font-mono">
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* File List */}
          <div className="flex-1 overflow-y-auto">
            {filteredDocuments.length === 0 ? (
              <div className="p-6 text-center">
                <FiFileText className="w-8 h-8 text-gray-700 mx-auto mb-2" />
                <p className="text-gray-500 text-xs font-mono">
                  {searchQuery ? 'No matches' : 'No documents'}
                </p>
              </div>
            ) : (
              filteredDocuments.map((doc, index) => (
                <div
                  key={doc.id}
                  onClick={() => setSelectedDoc(doc)}
                  className={`flex items-start gap-2 px-3 py-2.5 cursor-pointer border-b border-gray-800/30 transition-colors ${
                    selectedDoc?.id === doc.id
                      ? 'bg-cyan-500/10 border-l-2 border-l-cyan-500'
                      : 'hover:bg-gray-800/50 border-l-2 border-l-transparent'
                  } ${selectedIds.has(doc.id) ? 'bg-cyan-900/15' : ''}`}
                >
                  {/* Checkbox */}
                  <button
                    onClick={(e) => handleCheckboxClick(doc.id, index, e)}
                    className="mt-0.5 p-0.5 flex-shrink-0"
                  >
                    {selectedIds.has(doc.id) ? (
                      <FiCheckSquare className="w-3.5 h-3.5 text-cyan-400" />
                    ) : (
                      <FiSquare className="w-3.5 h-3.5 text-gray-700 hover:text-gray-500" />
                    )}
                  </button>

                  {/* File info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] text-white font-mono truncate leading-tight" title={doc.suggested_copy_destination_name || doc.filename}>
                      {doc.suggested_copy_destination_name || doc.filename}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[9px] text-gray-500 font-mono">
                        {safeFormatDate(doc.document_date) !== '-'
                          ? safeFormatDate(doc.document_date)
                          : safeFormatDate(doc.extracted_data?.date) !== '-'
                            ? safeFormatDate(doc.extracted_data?.date)
                            : safeFormatDate(doc.created_at)}
                      </span>
                      {doc.file_size && (
                        <span className="text-[9px] text-gray-600 font-mono">
                          {(doc.file_size / 1024).toFixed(0)}KB
                        </span>
                      )}
                    </div>
                    {doc.document_type && (
                      <div className="mt-1">
                        {getDocumentTypeBadge(doc.document_type)}
                      </div>
                    )}
                  </div>

                  {/* Quick actions */}
                  <div className="flex flex-col gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDownload(doc); }}
                      className="p-1 text-gray-600 hover:text-green-400 transition-colors"
                      title="Download"
                    >
                      <FiDownload className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right: Document Preview */}
        <div className="flex-1 flex flex-col bg-gray-950">
          {selectedDoc ? (
            <>
              {/* Preview toolbar */}
              <div className="flex items-center justify-between px-4 py-2 border-b border-gray-800 bg-gray-900/30">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => navigateDoc('prev')}
                    disabled={currentDocIndex <= 0}
                    className="p-1 text-gray-500 hover:text-white disabled:text-gray-700 disabled:cursor-not-allowed transition-colors"
                    title="Previous document"
                  >
                    <FiChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-[10px] font-mono text-gray-500">
                    {currentDocIndex + 1} / {filteredDocuments.length}
                  </span>
                  <button
                    onClick={() => navigateDoc('next')}
                    disabled={currentDocIndex >= filteredDocuments.length - 1}
                    className="p-1 text-gray-500 hover:text-white disabled:text-gray-700 disabled:cursor-not-allowed transition-colors"
                    title="Next document"
                  >
                    <FiChevronRight className="w-4 h-4" />
                  </button>
                </div>

                <div className="flex items-center gap-1">
                  <span className="text-[10px] font-mono text-gray-600 truncate max-w-md mr-2">
                    {selectedDoc.suggested_copy_destination_name || selectedDoc.filename}
                  </span>
                  <button
                    onClick={() => setFullscreenDoc(selectedDoc)}
                    className="p-1.5 text-gray-500 hover:text-cyan-400 transition-colors"
                    title="Fullscreen viewer"
                  >
                    <FiMaximize2 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleDownload(selectedDoc)}
                    className="p-1.5 text-gray-500 hover:text-green-400 transition-colors"
                    title="Download"
                  >
                    <FiDownload className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleDelete(selectedDoc)}
                    className="p-1.5 text-gray-500 hover:text-red-400 transition-colors"
                    title="Delete"
                  >
                    <FiTrash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {/* Embedded DocumentViewer */}
              <div className="flex-1 overflow-hidden">
                <DocumentViewer
                  key={selectedDoc.id}
                  document={selectedDoc}
                  onClose={() => setSelectedDoc(null)}
                  onDocumentUpdate={loadData}
                  isEmbedded={true}
                />
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <FiFileText className="w-12 h-12 text-gray-800 mx-auto mb-3" />
                <p className="text-gray-600 font-mono text-sm">Select a document to preview</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fullscreen Document Viewer Modal */}
      {fullscreenDoc && (
        <DocumentViewer
          document={fullscreenDoc}
          onClose={() => {
            setFullscreenDoc(null);
            loadData();
          }}
          onDocumentUpdate={loadData}
        />
      )}

      {/* Email Documents Modal */}
      {showEmailModal && (
        <EmailDocumentsModal
          documents={selectedDocuments}
          propertyName={property?.name || property?.address || 'Property'}
          onClose={() => setShowEmailModal(false)}
          onSuccess={handleEmailSuccess}
        />
      )}
    </div>
  );
};

export default PropertyDocuments;
